import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions } from '@/data/sources';
import {
  fetchTrackingCompetitors,
  fetchTrackingDistribution,
  fetchTrackingKeywordMap,
  fetchTrackingSummary,
  fetchTrackingWeeks,
} from '@/data/sources-tracking';
import type {
  TrackingCompetitor,
  TrackingDistributionDomain,
} from '@/types/seo-tracking';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/competitor-tracking — Position Tracking — Competitors (Project B, Lane C).
 *
 * Four tabs over the weekly SERP competitor-tracking aggregates (spec §5/§6):
 * Overview · Competitors Discovery · Rankings Distribution · Keyword Map, with a
 * persistent week selector (+ domain scope). Every read here is FREE + read-only
 * (spec §5.3): pure aggregates over stored rows, NEVER a provider call, NEVER a
 * charge — so plain useQuery-on-mount is correct (unlike the paid SEO surfaces).
 *
 * HONESTY: null aggregates render as an em-dash; est_traffic is honest NULL in
 * v1 (spec D11). Diff sign convention: POSITIVE = improved (position moved DOWN
 * numerically) — matches the merged rank-arrow fix (142214b), green ▲ = better.
 * Consumes ONLY the locked §5.3 endpoint contract — no invented shapes.
 */

/* --- visibility weight table (spec §5.1, basis points of 10000) ------------- */
function weightBp(position: number | null): number {
  if (position === null) return 0;
  if (position === 1) return 3200;
  if (position === 2) return 1600;
  if (position === 3) return 1000;
  if (position === 4) return 700;
  if (position === 5) return 600;
  if (position === 6) return 500;
  if (position === 7) return 400;
  if (position === 8) return 300;
  if (position === 9) return 250;
  if (position === 10) return 200;
  if (position <= 20) return 100;
  if (position <= 50) return 50;
  if (position <= 100) return 20;
  return 0;
}

/** Per-keyword contribution to the own-domain visibility % (spec §5.1 basis). */
function visibilityContribution(position: number | null, keywordsCaptured: number): number | null {
  if (position === null || keywordsCaptured <= 0) return null;
  return (weightBp(position) / (keywordsCaptured * 10000)) * 100;
}

/* --- honest formatters ------------------------------------------------------ */
function orNum(v: number | null, dp = 0): string {
  return v === null ? '—' : v.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function orPct(v: number | null, dp = 2): string {
  return v === null ? '—' : `${v.toFixed(dp)}%`;
}
function orPos(v: number | null): string {
  return v === null ? '—' : `#${v}`;
}

type DiffTone = 'up' | 'down' | 'flat' | 'na';
/** positive diff = improved (green ▲); negative = declined (red ▼). */
function diffLabel(diff: number | null, dp = 0): { text: string; tone: DiffTone } {
  if (diff === null) return { text: '—', tone: 'na' };
  const mag = Math.abs(diff).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  if (diff > 0) return { text: `▲ ${mag}`, tone: 'up' };
  if (diff < 0) return { text: `▼ ${mag}`, tone: 'down' };
  return { text: `– 0`, tone: 'flat' };
}
function toneClass(tone: DiffTone): string {
  return tone === 'up' ? 'text-success' : tone === 'down' ? 'text-danger' : 'text-text-tertiary';
}

function DiffPill({ diff, dp = 0 }: { diff: number | null; dp?: number }) {
  const d = diffLabel(diff, dp);
  return <span className={`font-mono text-xs ${toneClass(d.tone)}`}>{d.text}</span>;
}

/* --- stat tile -------------------------------------------------------------- */
function StatTile({
  label,
  value,
  diff,
  diffDp = 0,
  sub,
}: {
  label: string;
  value: string;
  diff?: number | null;
  diffDp?: number;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface p-3">
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-mono text-xl font-semibold text-text-primary" data-tabular>
          {value}
        </span>
        {diff !== undefined && <DiffPill diff={diff} dp={diffDp} />}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-text-tertiary">{sub}</div>}
    </div>
  );
}

/* --- competition-map bubble chart (inline SVG, no chart dependency) ---------
 * x = keyword_count · y = avg_position INVERTED (better = up) · r ∝ √visibility.
 * Domains without a numeric avg_position are honestly omitted (no position to
 * plot) and noted below the chart — never plotted at a fabricated coordinate. */
function CompetitionMap({ competitors }: { competitors: TrackingCompetitor[] }) {
  const W = 640;
  const H = 340;
  const PAD = { top: 18, right: 18, bottom: 40, left: 48 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const plottable = competitors.filter(
    (c) => c.avg_position !== null && c.keyword_count !== null,
  );
  const omitted = competitors.length - plottable.length;

  if (plottable.length === 0) {
    return (
      <p className="text-sm text-text-tertiary">
        No domain has a numeric average position for this week yet — nothing to plot. The bubble
        map appears once the weekly SERP capture records ranked positions.
      </p>
    );
  }

  const maxKw = Math.max(1, ...plottable.map((c) => c.keyword_count ?? 0));
  const positions = plottable.map((c) => c.avg_position ?? 0);
  const minPos = Math.min(...positions);
  const maxPos = Math.max(...positions);
  const posSpan = Math.max(1, maxPos - minPos);
  const maxVis = Math.max(0.01, ...plottable.map((c) => c.visibility_pct ?? 0));

  const xOf = (kw: number) => PAD.left + (kw / maxKw) * plotW;
  // invert: better (lower) position sits HIGHER on the chart (smaller y pixel).
  const yOf = (pos: number) => PAD.top + ((pos - minPos) / posSpan) * plotH;
  const rOf = (vis: number) => 5 + Math.sqrt(Math.max(0, vis) / maxVis) * 22;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full min-w-[520px]"
        role="img"
        aria-label="Competition map: average position versus number of ranked keywords, bubble size is visibility"
      >
        {/* axes */}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + plotH} className="stroke-current text-border-default" strokeWidth={1} />
        <line x1={PAD.left} y1={PAD.top + plotH} x2={PAD.left + plotW} y2={PAD.top + plotH} className="stroke-current text-border-default" strokeWidth={1} />
        {/* y hint: better up */}
        <text x={12} y={PAD.top + 4} className="fill-current text-text-tertiary" fontSize={10}>
          #{minPos.toFixed(0)}
        </text>
        <text x={12} y={PAD.top + plotH} className="fill-current text-text-tertiary" fontSize={10}>
          #{maxPos.toFixed(0)}
        </text>
        <text
          x={-(PAD.top + plotH / 2)}
          y={14}
          transform="rotate(-90)"
          textAnchor="middle"
          className="fill-current text-text-tertiary"
          fontSize={10}
        >
          Avg position (better ↑)
        </text>
        <text x={PAD.left + plotW / 2} y={H - 6} textAnchor="middle" className="fill-current text-text-tertiary" fontSize={10}>
          Ranked keywords →
        </text>
        {/* bubbles */}
        {plottable.map((c) => {
          const cx = xOf(c.keyword_count ?? 0);
          const cy = yOf(c.avg_position ?? 0);
          const r = rOf(c.visibility_pct ?? 0);
          return (
            <g key={c.domain}>
              <circle
                cx={cx}
                cy={cy}
                r={r}
                className={c.is_own ? 'fill-current text-engine' : 'fill-current text-ai'}
                fillOpacity={c.is_own ? 0.55 : 0.3}
                stroke="currentColor"
                strokeWidth={c.is_own ? 2 : 1}
              />
              <title>{`${c.domain} — avg #${(c.avg_position ?? 0).toFixed(1)}, ${c.keyword_count ?? 0} kw, visibility ${orPct(c.visibility_pct)}`}</title>
            </g>
          );
        })}
      </svg>
      <p className="mt-1 text-[11px] text-text-tertiary">
        Bubble size ∝ visibility. Your domain is highlighted.
        {omitted > 0 && ` ${omitted} domain${omitted === 1 ? '' : 's'} without a ranked position this week omitted.`}
      </p>
    </div>
  );
}

/* --- rankings distribution stacked weekly bars (inline SVG) ------------------ */
function DistributionBars({ domain }: { domain: TrackingDistributionDomain }) {
  const pts = domain.series;
  if (pts.length === 0) return <p className="text-xs text-text-tertiary">No weekly series.</p>;

  // cumulative buckets -> disjoint segments (top3, 4-10, 11-20, 21-100).
  const segs = pts.map((p) => {
    const t3 = p.top3 ?? 0;
    const t10 = p.top10 ?? 0;
    const t20 = p.top20 ?? 0;
    const t100 = p.top100 ?? 0;
    return {
      week: p.week,
      s: [t3, Math.max(0, t10 - t3), Math.max(0, t20 - t10), Math.max(0, t100 - t20)],
      total: t100,
    };
  });
  const maxTotal = Math.max(1, ...segs.map((s) => s.total));
  const barW = 26;
  const gap = 16;
  const chartH = 96;
  const W = segs.length * (barW + gap) + gap;
  const H = chartH + 20;
  const segColors = ['text-success', 'text-ai', 'text-engine', 'text-text-tertiary'];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[420px]" role="img" aria-label={`Weekly ranking distribution for ${domain.domain}`}>
      {segs.map((seg, i) => {
        const x = gap + i * (barW + gap);
        let yCursor = chartH;
        return (
          <g key={seg.week}>
            {seg.s.map((count, si) => {
              const h = (count / maxTotal) * chartH;
              yCursor -= h;
              return (
                <rect
                  key={si}
                  x={x}
                  y={yCursor}
                  width={barW}
                  height={h}
                  className={`fill-current ${segColors[si]}`}
                  fillOpacity={0.85}
                >
                  <title>{`${domain.domain} · ${seg.week}: bucket ${['Top 3', '4–10', '11–20', '21–100'][si]} = ${count}`}</title>
                </rect>
              );
            })}
            <text x={x + barW / 2} y={H - 6} textAnchor="middle" className="fill-current text-text-tertiary" fontSize={8}>
              {seg.week.slice(5)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* --- tabs ------------------------------------------------------------------- */
type TabKey = 'overview' | 'competitors' | 'distribution' | 'keyword-map';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'competitors', label: 'Competitors Discovery' },
  { key: 'distribution', label: 'Rankings Distribution' },
  { key: 'keyword-map', label: 'Keyword Map' },
];

const inputClass =
  'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

export function SeoCompetitorTrackingPage() {
  const navigate = useNavigate();

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();
  const projectId = resolveActiveProjectId(active, projects);

  const [tab, setTab] = useState<TabKey>('overview');
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [pickedDomains, setPickedDomains] = useState<string[]>([]);

  const weeksQ = useQuery({
    queryKey: ['seo', 'tracking', 'weeks', projectId],
    queryFn: () => fetchTrackingWeeks(projectId as string),
    enabled: projectId !== null,
  });
  const week = selectedWeek ?? weeksQ.data?.latest ?? undefined;

  const summaryQ = useQuery({
    queryKey: ['seo', 'tracking', 'summary', projectId, week],
    queryFn: () => fetchTrackingSummary(projectId as string, week),
    enabled: projectId !== null,
  });
  const competitorsQ = useQuery({
    queryKey: ['seo', 'tracking', 'competitors', projectId, week],
    queryFn: () => fetchTrackingCompetitors(projectId as string, week, 25),
    enabled: projectId !== null,
  });
  const distributionDomains = pickedDomains.length > 0 ? pickedDomains.join(',') : undefined;
  const distributionQ = useQuery({
    queryKey: ['seo', 'tracking', 'distribution', projectId, distributionDomains],
    queryFn: () => fetchTrackingDistribution(projectId as string, 8, distributionDomains),
    enabled: projectId !== null,
  });
  const keywordMapQ = useQuery({
    queryKey: ['seo', 'tracking', 'keyword-map', projectId, week],
    queryFn: () => fetchTrackingKeywordMap(projectId as string, week),
    enabled: projectId !== null,
  });

  /* ----- gates (all hooks are declared above; these are pure branches) ----- */
  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Position Tracking — Competitors" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Position Tracking — Competitors" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Position Tracking — Competitors" />
        <EmptyState
          title="No project to track competitors for"
          teach="Weekly competitor tracking runs against an SEO project. Add your first domain on the Projects page, then the weekly SERP capture builds this view."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const weeks = weeksQ.data?.weeks ?? [];
  const hasNoData = weeksQ.isSuccess && weeks.length === 0;

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => {
          void select(Number(e.target.value));
          setSelectedWeek(null);
          setPickedDomains([]);
        }}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  const weekSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Week</span>
      <select
        value={week ?? ''}
        onChange={(e) => setSelectedWeek(e.target.value || null)}
        disabled={weeks.length === 0}
        className={inputClass}
        aria-label="Tracking week"
      >
        {weeks.length === 0 && <option value="">—</option>}
        {weeks.map((w) => (
          <option key={w} value={w}>
            {w}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Position Tracking — Competitors"
        subtitle="Weekly SERP snapshot of who ranks alongside you for your tracked keywords — computed from stored captures, free to view, and honest about every missing metric."
        actions={
          <>
            {projectSelector}
            {weekSelector}
          </>
        }
      />

      {/* tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-border-subtle">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-ai text-text-primary'
                : 'border-transparent text-text-tertiary hover:text-text-secondary'
            }`}
            aria-current={tab === t.key ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>

      {hasNoData ? (
        <EmptyState
          title="No tracking data yet"
          teach="The weekly competitor capture hasn't recorded a week for this project yet. Once it runs (Mondays), this page fills with per-keyword competitor positions, visibility and rankings distribution."
        />
      ) : (
        <>
          {tab === 'overview' && (
            <OverviewTab summaryQ={summaryQ} keywordMapQ={keywordMapQ} />
          )}
          {tab === 'competitors' && <CompetitorsTab competitorsQ={competitorsQ} />}
          {tab === 'distribution' && (
            <DistributionTab
              distributionQ={distributionQ}
              competitorsQ={competitorsQ}
              picked={pickedDomains}
              onToggle={(d) =>
                setPickedDomains((prev) =>
                  prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
                )
              }
              onClear={() => setPickedDomains([])}
            />
          )}
          {tab === 'keyword-map' && <KeywordMapTab keywordMapQ={keywordMapQ} />}
        </>
      )}
    </div>
  );
}

/* ===== Overview tab ========================================================= */
function OverviewTab({
  summaryQ,
  keywordMapQ,
}: {
  summaryQ: ReturnType<typeof useQuery>;
  keywordMapQ: ReturnType<typeof useQuery>;
}) {
  if (summaryQ.isPending) return <LoadingState rows={4} />;
  if (summaryQ.error) return <ErrorState error={summaryQ.error} onRetry={() => void summaryQ.refetch()} />;
  const s = summaryQ.data as import('@/types/seo-tracking').TrackingSummary;
  const own = s.own;
  const km = keywordMapQ.data as import('@/types/seo-tracking').TrackingKeywordMap | undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-tertiary">
        <Badge tone="neutral">Your domain: {s.own_domain}</Badge>
        <span>
          Week {s.week ?? '—'}
          {s.prev_week ? ` vs ${s.prev_week}` : ''} · {s.keywords_captured} of {s.tracked_keywords} keywords
          captured · source {s.source}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Visibility" value={orPct(own.visibility_pct)} diff={own.visibility_diff} diffDp={2} />
        <StatTile label="Avg position" value={orNum(own.avg_position, 2)} diff={own.avg_position_diff} diffDp={2} />
        <StatTile label="Keywords captured" value={orNum(s.keywords_captured)} sub={`of ${s.tracked_keywords} tracked`} />
        <StatTile label="Improved / declined" value={`${orNum(own.improved)} / ${orNum(own.declined)}`} sub="vs previous week" />
        <StatTile label="New / lost" value={`${orNum(own.new_count)} / ${orNum(own.lost)}`} sub="entered / dropped top-100" />
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge tone="neutral">Top 3: {orNum(own.top3)}</Badge>
          <Badge tone="neutral">Top 10: {orNum(own.top10)}</Badge>
          <Badge tone="neutral">Top 20: {orNum(own.top20)}</Badge>
          <Badge tone="neutral">Top 100: {orNum(own.top100)}</Badge>
        </div>
      </Card>

      <div>
        <div className="mb-2 text-sm font-medium text-text-primary">Your keywords this week</div>
        {keywordMapQ.isPending ? (
          <LoadingState rows={3} />
        ) : keywordMapQ.error ? (
          <ErrorState error={keywordMapQ.error} onRetry={() => void keywordMapQ.refetch()} />
        ) : !km || km.keywords.length === 0 ? (
          <p className="text-sm text-text-tertiary">No per-keyword rows captured for this week yet.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] border-collapse">
                <thead>
                  <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                    <th className="px-3 py-2 font-semibold">Keyword</th>
                    <th className="px-3 py-2 text-right font-semibold">Position</th>
                    <th className="px-3 py-2 text-right font-semibold">Previous</th>
                    <th className="px-3 py-2 text-right font-semibold">Change</th>
                    <th className="px-3 py-2 text-right font-semibold">Visibility contrib.</th>
                    <th className="px-3 py-2 font-semibold">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {km.keywords.map((k) => {
                    const pos = k.own?.position ?? null;
                    const contrib = visibilityContribution(pos, s.keywords_captured);
                    return (
                      <tr key={k.keyword_id} className="border-b border-border-subtle last:border-0 hover:bg-hover">
                        <td className="px-3 py-2.5 text-sm font-medium text-text-primary">{k.keyword}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-sm text-text-primary" data-tabular>{orPos(pos)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>{orPos(k.own?.prev_position ?? null)}</td>
                        <td className="px-3 py-2.5 text-right"><DiffPill diff={k.own?.diff ?? null} /></td>
                        <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>{orPct(contrib)}</td>
                        <td className="px-3 py-2.5 text-sm">
                          {k.own?.serp_url ? (
                            <a href={k.own.serp_url} target="_blank" rel="noreferrer" className="text-engine hover:underline">
                              View
                            </a>
                          ) : (
                            <span className="text-text-tertiary">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
              Visibility contribution derived from the BookedCentral CTR-weight curve · a null position means
              the keyword was captured but your domain was not in the top 100 — shown honestly, never as zero.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== Competitors Discovery tab =========================================== */
const NUM_CELL = 'font-mono text-sm text-text-secondary';

function CompetitorsTab({ competitorsQ }: { competitorsQ: ReturnType<typeof useQuery> }) {
  if (competitorsQ.isPending) return <LoadingState rows={4} />;
  if (competitorsQ.error) return <ErrorState error={competitorsQ.error} onRetry={() => void competitorsQ.refetch()} />;
  const data = competitorsQ.data as import('@/types/seo-tracking').TrackingCompetitors;
  if (data.competitors.length === 0) {
    return <EmptyState title="No competitors captured" teach="The weekly capture found no ranking domains for this week yet." />;
  }

  // You-row pinned first (default natural order; user sorting may reorder).
  const own = data.competitors.find((c) => c.is_own);
  const rows = [
    ...data.competitors.filter((c) => c.is_own),
    ...data.competitors.filter((c) => !c.is_own),
  ];

  const columns: SortableColumn<TrackingCompetitor>[] = [
    {
      key: 'domain',
      header: 'Domain',
      sortable: true,
      sortValue: (c) => c.domain,
      render: (c) => (
        <span className="flex items-center gap-1.5">
          <span className={c.is_own ? 'font-semibold text-text-primary' : 'text-text-primary'}>{c.domain}</span>
          {c.is_own && <Badge tone="engine">You</Badge>}
        </span>
      ),
      truncate: true,
      maxWidthClass: 'max-w-[240px]',
      title: (c) => c.domain,
      cellClassName: 'font-medium',
    },
    {
      key: 'visibility_pct',
      header: 'Visibility',
      align: 'right',
      numeric: true,
      sortable: true,
      sortValue: (c) => c.visibility_pct,
      render: (c) => (
        <span className="flex items-center justify-end gap-2">
          <span className="font-mono text-text-primary">{orPct(c.visibility_pct)}</span>
          <DiffPill diff={c.visibility_diff} dp={2} />
        </span>
      ),
    },
    {
      key: 'est_traffic',
      header: 'Est. traffic',
      align: 'right',
      numeric: true,
      sortable: true,
      sortValue: (c) => c.est_traffic,
      render: (c) => orNum(c.est_traffic),
      cellClassName: NUM_CELL,
    },
    {
      key: 'keyword_count',
      header: 'Keywords',
      align: 'right',
      numeric: true,
      sortable: true,
      sortValue: (c) => c.keyword_count,
      render: (c) => orNum(c.keyword_count),
      cellClassName: `${NUM_CELL} text-text-primary`,
    },
    {
      key: 'avg_position',
      header: 'Avg. pos ± σ',
      align: 'right',
      numeric: true,
      sortable: true,
      sortValue: (c) => c.avg_position,
      render: (c) => `${orNum(c.avg_position, 1)} ± ${orNum(c.position_stddev, 1)}`,
      cellClassName: NUM_CELL,
    },
  ];

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="mb-1 text-sm font-medium text-text-primary">Competition map</div>
        <p className="mb-3 text-xs text-text-tertiary">
          Average position (better = higher) against the number of ranked keywords, bubble size ∝ visibility.
        </p>
        <CompetitionMap competitors={data.competitors} />
      </Card>

      {own && (
        <div className="rounded-md border border-engine/30 bg-engine/5 px-3 py-2 text-sm text-text-secondary">
          <span className="font-semibold text-text-primary">{own.domain}</span> — rank #{own.rank} of {data.total_domains} ·
          visibility {orPct(own.visibility_pct)} · {orNum(own.keyword_count)} ranked keywords.
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
        <SortableTable
          rows={rows}
          columns={columns}
          getRowKey={(c) => c.domain}
          minWidthClass="min-w-[640px]"
          caption="Competing domains by visibility, with keyword counts and average position"
        />
        <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
          {data.total_domains} domains ranked this week · your domain always shown · estimated traffic is not
          available in v1 (no competitor volume source yet) — shown as an em-dash, never invented.
        </div>
      </div>
    </div>
  );
}

/* ===== Rankings Distribution tab =========================================== */
function DistributionTab({
  distributionQ,
  competitorsQ,
  picked,
  onToggle,
  onClear,
}: {
  distributionQ: ReturnType<typeof useQuery>;
  competitorsQ: ReturnType<typeof useQuery>;
  picked: string[];
  onToggle: (d: string) => void;
  onClear: () => void;
}) {
  const comp = competitorsQ.data as import('@/types/seo-tracking').TrackingCompetitors | undefined;
  const candidates = (comp?.competitors ?? []).slice(0, 12).map((c) => ({ domain: c.domain, is_own: c.is_own }));

  return (
    <div className="space-y-4">
      {candidates.length > 0 && (
        <Card className="p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-text-secondary">Domains</span>
            {picked.length > 0 && (
              <button type="button" onClick={onClear} className="text-[11px] text-text-tertiary hover:text-text-secondary">
                Reset to default
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {candidates.map((c) => {
              const on = picked.includes(c.domain);
              return (
                <label
                  key={c.domain}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${
                    on ? 'border-ai bg-ai/10 text-text-primary' : 'border-border-subtle text-text-secondary'
                  }`}
                >
                  <input type="checkbox" checked={on} onChange={() => onToggle(c.domain)} className="accent-ai" />
                  {c.domain}
                  {c.is_own && <Badge tone="engine">You</Badge>}
                </label>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-text-tertiary">
            {picked.length === 0
              ? 'Showing the default set (top domains by visibility + your domain).'
              : `Showing ${picked.length} selected domain${picked.length === 1 ? '' : 's'}.`}
          </p>
        </Card>
      )}

      {distributionQ.isPending ? (
        <LoadingState rows={4} />
      ) : distributionQ.error ? (
        <ErrorState error={distributionQ.error} onRetry={() => void distributionQ.refetch()} />
      ) : (
        (() => {
          const dist = distributionQ.data as import('@/types/seo-tracking').TrackingDistribution;
          if (dist.domains.length === 0) {
            return <EmptyState title="No distribution series" teach="No weekly ranking distribution has been recorded for these domains yet." />;
          }
          return (
            <div className="space-y-3">
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] border-collapse">
                    <thead>
                      <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                        <th className="px-3 py-2 font-semibold">Domain</th>
                        <th className="px-3 py-2 text-right font-semibold">Visibility (latest)</th>
                        <th className="px-3 py-2 text-right font-semibold">Top 3</th>
                        <th className="px-3 py-2 text-right font-semibold">Top 10</th>
                        <th className="px-3 py-2 text-right font-semibold">Top 20</th>
                        <th className="px-3 py-2 text-right font-semibold">Top 100</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dist.domains.map((d) => {
                        const latest = d.series[d.series.length - 1];
                        return (
                          <tr key={d.domain} className="border-b border-border-subtle last:border-0 hover:bg-hover">
                            <td className="px-3 py-2.5 text-sm font-medium text-text-primary">
                              <span className="flex items-center gap-1.5">
                                {d.domain}
                                {d.is_own && <Badge tone="engine">You</Badge>}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono text-sm text-text-primary" data-tabular>{orPct(latest?.visibility_pct ?? null)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>{orNum(latest?.top3 ?? null)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>{orNum(latest?.top10 ?? null)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>{orNum(latest?.top20 ?? null)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>{orNum(latest?.top100 ?? null)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {dist.domains.map((d) => (
                  <Card key={d.domain} className="p-3">
                    <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-text-primary">
                      {d.domain}
                      {d.is_own && <Badge tone="engine">You</Badge>}
                    </div>
                    <DistributionBars domain={d} />
                  </Card>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 text-[11px] text-text-tertiary">
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-success" />Top 3</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-ai" />4–10</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-engine" />11–20</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-text-tertiary" />21–100</span>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}

/* ===== Keyword Map tab ====================================================== */
function KeywordMapTab({ keywordMapQ }: { keywordMapQ: ReturnType<typeof useQuery> }) {
  if (keywordMapQ.isPending) return <LoadingState rows={4} />;
  if (keywordMapQ.error) return <ErrorState error={keywordMapQ.error} onRetry={() => void keywordMapQ.refetch()} />;
  const km = keywordMapQ.data as import('@/types/seo-tracking').TrackingKeywordMap;
  if (km.keywords.length === 0) {
    return <EmptyState title="No keyword map yet" teach="No per-keyword competitor positions have been captured for this week yet." />;
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-text-tertiary">
        Week {km.week ?? '—'}
        {km.prev_week ? ` vs ${km.prev_week}` : ''} · green ▲ = improved, red ▼ = declined.
      </div>
      {km.keywords.map((k) => (
        <Card key={k.keyword_id} className="p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-text-primary">{k.keyword}</span>
            <Badge tone="engine">
              You: {orPos(k.own?.position ?? null)}
            </Badge>
            <DiffPill diff={k.own?.diff ?? null} />
          </div>
          {k.competitors.length === 0 ? (
            <p className="text-xs text-text-tertiary">No competitor captured for this keyword this week.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {k.competitors.map((c) => {
                const d = diffLabel(c.diff);
                return (
                  <span
                    key={c.domain}
                    className="flex items-center gap-1.5 rounded-md border border-border-subtle bg-inset/40 px-2 py-1 text-xs"
                    title={`${c.domain}: #${c.position ?? '—'} (was #${c.prev_position ?? '—'})`}
                  >
                    <span className="text-text-secondary">{c.domain}</span>
                    <span className="font-mono text-text-primary">{orPos(c.position)}</span>
                    <span className={`font-mono ${toneClass(d.tone)}`}>{d.text}</span>
                  </span>
                );
              })}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
